import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building, 
  CheckCircle, 
  XCircle, 
  Calendar, 
  Image as ImageIcon, 
  Globe, 
  Search, 
  Grid, 
  List,
  Eye,
  Filter,
  Users,
  BookOpen
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/GalleryAdmin.css';
import '../../styles/CuratorGalleries.css';

const CuratorGalleries = () => {
  const navigate = useNavigate();
  
  // Mobile detection utility
  const isMobile = () => {
    return window.innerWidth <= 768;
  };
  
  // State management
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [approvedFilter, setApprovedFilter] = useState('true');
  const [viewMode, setViewMode] = useState(() => isMobile() ? 'list' : 'grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalGalleries, setTotalGalleries] = useState(0);
  const [selectedGalleries, setSelectedGalleries] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    recent: 0,
    galleries_with_images: 0,
    galleries_with_websites: 0
  });

  // Curator-specific state
  const [curatorGalleryIds, setCuratorGalleryIds] = useState([]);
  const [userName, setUserName] = useState('');
  const [curatorDataLoaded, setCuratorDataLoaded] = useState(false);
  const [curatorGalleryTypes, setCuratorGalleryTypes] = useState([]);

  // Get personalized greeting based on time of day
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Get greeting emoji based on time
  const getGreetingEmoji = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '🌅';
    if (hour < 17) return '☀️';
    return '🌙';
  };

  // Gallery types with counts (from your actual data)
  const galleryTypes = [
    { value: 'gallery', label: 'Gallery', count: 278 },
    { value: 'public space', label: 'Public Space', count: 72 },
    { value: 'fair', label: 'Fair', count: 47 },
    { value: 'studio', label: 'Studio', count: 12 },
    { value: 'restaurant/bar', label: 'Restaurant/Bar', count: 11 },
    { value: 'Private Studio', label: 'Private Studio', count: 10 },
    { value: 'event venue', label: 'Event Venue', count: 9 },
    { value: 'online', label: 'Online', count: 1 },
    { value: 'Gallery', label: 'Gallery', count: 1 },
    { value: 'Museum', label: 'Museum', count: 1 },
    { value: 'Pop Up', label: 'Pop Up', count: 1 }
  ];

  // Load curator data first
  useEffect(() => {
    const loadCuratorData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/login');
          return;
        }

        // Get user profile for name
        const { data: profile } = await supabase
          .from('users')
          .select('first_name, last_name, nickname')
          .eq('id', user.id)
          .single();
        
        if (profile) {
          const displayName = profile.nickname || profile.first_name || 'there';
          setUserName(displayName);
        }

        // Get curator's gallery access
        const { data: galleryAccess, error: accessError } = await supabase
          .from('gallery_access')
          .select('gallery_id')
          .eq('user_uuid', user.id);

        if (accessError) throw accessError;
        
        if (galleryAccess && galleryAccess.length > 0) {
          const galleryIds = galleryAccess.map(access => access.gallery_id);
          setCuratorGalleryIds(galleryIds);
          console.log('Curator has access to galleries:', galleryIds);
        } else {
          console.log('No gallery access found for curator');
          setCuratorGalleryIds([]);
        }

        setCuratorDataLoaded(true);
      } catch (err) {
        console.error('Error loading curator data:', err);
        setCuratorDataLoaded(true);
      }
    };

    loadCuratorData();
  }, [navigate]);

  useEffect(() => {
    if (curatorDataLoaded) {
      loadGalleries();
      loadGalleryStats();
    }
  }, [searchQuery, typeFilter, approvedFilter, currentPage, curatorDataLoaded, curatorGalleryIds]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, typeFilter, approvedFilter]);

  // Handle mobile view mode on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isMobile() && viewMode === 'grid') {
        setViewMode('list');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  const loadGalleries = async () => {
    try {
      setLoading(true);
      setError(null);

      // If curator has no gallery access, show empty state
      if (curatorGalleryIds.length === 0) {
        setGalleries([]);
        setTotalGalleries(0);
        setLoading(false);
        return;
      }

      // Build the base query for counting (without pagination) - filtered to curator's galleries
      let countQuery = supabase
        .from('galleries')
        .select('gallery_id', { count: 'exact', head: true })
        .in('gallery_id', curatorGalleryIds);

      // Build the main query for data - filtered to curator's galleries
      let query = supabase
        .from('galleries')
        .select(`
          gallery_id,
          name,
          type,
          description,
          cover_image,
          address,
          website,
          contact_email,
          approved,
          created_at,
          updated_at,
          added_by,
          users!galleries_added_by_fkey (
            nickname,
            first_name,
            last_name
          )
        `)
        .in('gallery_id', curatorGalleryIds)
        .order('name');

      // Add search filter to both queries
      if (searchQuery) {
        const searchFilter = `name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`;
        countQuery = countQuery.or(searchFilter);
        query = query.or(searchFilter);
      }

      // Add type filter to both queries
      if (typeFilter) {
        countQuery = countQuery.eq('type', typeFilter);
        query = query.eq('type', typeFilter);
      }

      // Add approved filter to both queries
      if (approvedFilter !== '') {
        countQuery = countQuery.eq('approved', approvedFilter === 'true');
        query = query.eq('approved', approvedFilter === 'true');
      }

      // Get the count first
      const { count: totalCount, error: countError } = await countQuery;
      if (countError) throw countError;

      // Add pagination to the main query
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error } = await query;
      if (error) throw error;

      // Get stats for each gallery: collections, artworks, artists
      const galleriesWithCounts = await Promise.all(
        (data || []).map(async (gallery) => {
          const galleryId = gallery.gallery_id || gallery.id;

          // Collections for this gallery
          const { data: galleryCollections, error: gcmError } = await supabase
            .from('gallery_collection_mapping')
            .select('collection_id')
            .eq('gallery_id', galleryId);
          if (gcmError) console.error('gallery_collection_mapping error', gcmError);
          const collectionIds = (galleryCollections || [])
            .map((c) => c.collection_id)
            .filter((id) => id != null);
          const collectionsCount = collectionIds.length;

          // Artworks in those collections (accepted only)
          let artworksCount = 0;
          let artistCount = 0;
          if (collectionIds.length > 0) {
            const { data: cvm, error: cvmError } = await supabase
              .from('collection_vc_mapping')
              .select('vc_id')
              .in('collection_id', collectionIds)
              .eq('accepted', true);
            if (cvmError) console.error('collection_vc_mapping error', cvmError);
            const vcIds = (cvm || [])
              .map((r) => r.vc_id)
              .filter((id) => id != null);

            if (vcIds.length > 0) {
              artworksCount = vcIds.length;
              // Get artists (distinct uuid) for those artworks
              const { data: vcsRows, error: vcsError } = await supabase
                .from('vcs')
                .select('uuid, vc_id')
                .in('vc_id', vcIds);
              if (vcsError) console.error('vcs fetch error', vcsError);
              const uniqueArtists = new Set((vcsRows || []).map((r) => r.uuid).filter(Boolean));
              artistCount = uniqueArtists.size;
            } else {
              // Fallback: some datasets store collection_id directly on vcs
              const { data: vcsRows, error: vcsFallbackError } = await supabase
                .from('vcs')
                .select('vc_id, uuid')
                .in('collection_id', collectionIds);
              if (vcsFallbackError) console.error('vcs fallback fetch error', vcsFallbackError);
              artworksCount = (vcsRows || []).length;
              const uniqueArtists = new Set((vcsRows || []).map((r) => r.uuid).filter(Boolean));
              artistCount = uniqueArtists.size;
            }
          }

          return {
            ...gallery,
            collections_count: collectionsCount,
            artworks_count: artworksCount,
            artists_count: artistCount,
            added_by_name: gallery.users?.nickname || 
                          `${gallery.users?.first_name || ''} ${gallery.users?.last_name || ''}`.trim() || 'Unknown'
          };
        })
      );

      setGalleries(galleriesWithCounts);
      setTotalGalleries(totalCount || 0);

      // Calculate curator-specific gallery types with counts
      const typeCounts = galleriesWithCounts.reduce((acc, gallery) => {
        const type = gallery.type || 'Unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});

      const curatorTypes = Object.entries(typeCounts).map(([type, count]) => ({
        value: type,
        label: type === 'Unknown' ? 'No Type' : type,
        count
      }));

      setCuratorGalleryTypes(curatorTypes);
    } catch (error) {
      console.error('Error loading galleries:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadGalleryStats = async () => {
    try {
      // If curator has no gallery access, show empty stats
      if (curatorGalleryIds.length === 0) {
        setStats({
          total: 0,
          approved: 0,
          pending: 0,
          recent: 0,
          galleries_with_images: 0,
          galleries_with_websites: 0
        });
        return;
      }

      // Get total galleries (curator's only)
      const { count: total } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true })
        .in('gallery_id', curatorGalleryIds);

      // Get approved galleries (curator's only)
      const { count: approved } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true })
        .in('gallery_id', curatorGalleryIds)
        .eq('approved', true);

      // Get pending galleries (curator's only)
      const { count: pending } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true })
        .in('gallery_id', curatorGalleryIds)
        .eq('approved', false);

      // Get recent galleries (last 30 days, curator's only)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { count: recent } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true })
        .in('gallery_id', curatorGalleryIds)
        .gte('created_at', thirtyDaysAgo.toISOString());

      // Get galleries with images (curator's only)
      const { count: galleriesWithImages } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true })
        .in('gallery_id', curatorGalleryIds)
        .not('cover_image', 'is', null);

      // Get galleries with websites (curator's only)
      const { count: galleriesWithWebsites } = await supabase
        .from('galleries')
        .select('*', { count: 'exact', head: true })
        .in('gallery_id', curatorGalleryIds)
        .not('website', 'is', null);

      setStats({
        total: total || 0,
        approved: approved || 0,
        pending: pending || 0,
        recent: recent || 0,
        galleries_with_images: galleriesWithImages || 0,
        galleries_with_websites: galleriesWithWebsites || 0
      });
    } catch (error) {
      console.error('Error loading gallery stats:', error);
    }
  };

  const handleDeleteGallery = async (galleryId) => {
    try {
      const { error } = await supabase
        .from('galleries')
        .delete()
        .eq('gallery_id', galleryId);

      if (error) throw error;

      // Reload galleries and stats
      loadGalleries();
      loadGalleryStats();
      setSelectedGalleries([]);
    } catch (error) {
      console.error('Error deleting gallery:', error);
      setError(error.message);
    }
  };

  const handleBulkDelete = async () => {
    try {
      for (const galleryId of selectedGalleries) {
        await handleDeleteGallery(galleryId);
      }
    } catch (error) {
      console.error('Error in bulk delete:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const StatsCard = ({ icon: Icon, label, value, color = 'blue', onClick, isActive }) => (
    <div 
      className={`stats-card stats-${color} ${onClick ? 'clickable' : ''} ${isActive ? 'active' : ''}`}
      onClick={onClick}
    >
      <div className="stats-icon">
        <Icon size={24} />
      </div>
      <div className="stats-content">
        <div className="stats-value">{value}</div>
        <div className="stats-label">{label}</div>
      </div>
    </div>
  );

  const handleStatsCardClick = (filterType) => {
    // Reset other filters when clicking a stats card
    setSearchQuery('');
    setTypeFilter('');
    setCurrentPage(1);
    
    switch (filterType) {
      case 'total':
        setApprovedFilter('');
        break;
      case 'approved':
        setApprovedFilter('true');
        break;
      case 'pending':
        setApprovedFilter('false');
        break;
      case 'recent':
        setApprovedFilter('true'); // Recent galleries are typically approved
        break;
      case 'with_images':
        setApprovedFilter('true');
        // Note: We could add a special filter for galleries with images
        break;
      case 'with_websites':
        setApprovedFilter('true');
        // Note: We could add a special filter for galleries with websites
        break;
      default:
        setApprovedFilter('true');
    }
  };

  const isStatsCardActive = (filterType) => {
    switch (filterType) {
      case 'total':
        return approvedFilter === '';
      case 'approved':
        return approvedFilter === 'true' && !searchQuery && !typeFilter;
      case 'pending':
        return approvedFilter === 'false' && !searchQuery && !typeFilter;
      case 'recent':
        return approvedFilter === 'true' && !searchQuery && !typeFilter;
      case 'with_images':
        return approvedFilter === 'true' && !searchQuery && !typeFilter;
      case 'with_websites':
        return approvedFilter === 'true' && !searchQuery && !typeFilter;
      default:
        return false;
    }
  };

  if (loading && galleries.length === 0) {
    return (
      <div className="gallery-admin">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading galleries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-admin">
      {/* Header (no card) */}
      <div className="galleries-header">
        <div className="header-content">
          <h1>{getGreetingEmoji()} {getTimeBasedGreeting()} {userName || 'Steve'}</h1>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="curator-tools-link"
            onClick={() => navigate('/artSpeaks')}
          >
            <Eye size={16} />
            <span>Open Art Speaks Directory</span>
          </button>
        </div>
      </div>

      {/* Compact toolbar: search + view toggle on one row */}
      <div className="galleries-toolbar">
        <div className="search-box">
          <span className="search-icon"><Search size={16} /></span>
          <input
            type="text"
            className="search-input"
            placeholder={`Search your ${stats.total} galleries by name or description...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="search-clear"
              onClick={() => setSearchQuery('')}
              type="button"
              aria-label="Clear search"
            >
              <XCircle size={16} />
            </button>
          )}
        </div>

        <div className="view-toggle">
          <button
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
            aria-pressed={viewMode === 'grid'}
          >
            <Grid size={16} />
          </button>
          <button
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
            aria-pressed={viewMode === 'list'}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* Gallery Display */}
      {galleries.length === 0 && !loading ? (
        <div className="no-results">
          <Building size={48} />
          <h3>No galleries found</h3>
          <p>Try adjusting your search criteria or filters</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="galleries-grid">
          {galleries.map(gallery => (
            <div 
              key={gallery.gallery_id || gallery.id} 
              className="gallery-card"
              onClick={() => navigate(`/curator/galleries/${gallery.gallery_id || gallery.id}`)}
            >
              <div className="gallery-image">
                {gallery.cover_image ? (
                  <img src={gallery.cover_image} alt={gallery.name} />
                ) : (
                  <div className="gallery-placeholder">
                    <Building size={48} />
                  </div>
                )}
              </div>
              
              <div className="gallery-content">
                <div className="gallery-header">
                  <h3>{gallery.name}</h3>
                </div>
                {gallery.address && (
                  <div className="gallery-address">{gallery.address}</div>
                )}

                <div className="gallery-stats compact">
                  <div className="stat-chip" title="Collections">
                    <BookOpen size={16} />
                    <span>{gallery.collections_count ?? 0}</span>
                    <span className="stat-label-text">Collections</span>
                  </div>
                  <div className="stat-chip" title="Artworks">
                    <ImageIcon size={16} />
                    <span>{gallery.artworks_count ?? 0}</span>
                    <span className="stat-label-text">Artworks</span>
                  </div>
                  <div className="stat-chip" title="Artists">
                    <Users size={16} />
                    <span>{gallery.artists_count ?? 0}</span>
                    <span className="stat-label-text">Artists</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="galleries-list-simple">
          {galleries.map(gallery => (
            <div 
              key={gallery.gallery_id || gallery.id} 
              className="gallery-row-simple"
              onClick={() => navigate(`/curator/galleries/${gallery.gallery_id || gallery.id}`)}
            >
              <span className="gallery-name-simple">{gallery.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalGalleries > pageSize && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalGalleries)} of {totalGalleries} galleries
          </div>
          <div className="pagination-controls">
            <button 
              className="btn-secondary"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {Math.ceil(totalGalleries / pageSize)}
            </span>
            <button 
              className="btn-secondary"
              onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalGalleries / pageSize), prev + 1))}
              disabled={currentPage === Math.ceil(totalGalleries / pageSize)}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default CuratorGalleries;
